import * as d3 from 'd3-selection'
import { interpolate } from 'd3-interpolate'
import Splitter from '../../path/splitter'
import { EPSILON } from 'chart-constants'
import { scaleLinear as scale } from 'd3-scale'
import * as shape from 'd3-shape'

const lineGenerator = shape.line()

/*
 * Transforms a line serie into a bar serie
 */
class LineBarTransformer {

  constructor (options) {
    this.serie = options.serie // The line serie we're turning into a bar
    this.yScale = options.yScale

    /*
     * Used to control the easing of each animation (line/bar)
     */
    this.ease = options.ease || (d => d) // default to no easing.

    let points = this.serie.selectAll('.point')
                           .data()

    this.splitter = new Splitter(points, this.serie.selectAll('path.line'), options.splitWidth, options.splitScale, {
      x: options.previous.scalers.x,
      yScale: options.previous.yScale
    }, {
      yScale: options.yScale
    }, options.lineScale)

    this.interpolaters = {
      stroke: interpolate(options.styles.line.stroke, options.styles.bar.stroke),
      strokeWidth: interpolate(options.styles.line.strokeWidth, options.styles.bar.strokeWidth),
      barOpacity: interpolate(EPSILON, 1), // Main bar fill
      pointOpacity: interpolate(1, EPSILON) // The dots on the line
    }

    this.fill = options.styles.bar.fill

    /*
     * Used to scale the timing variable `t`
     */
    this.scales = {
      // The line is transformed during the first 50% of the transition
      line: scale().domain([0, .5])
                   .range([0, 1])
                   .clamp(true),
      // Bar is transformed during the second 50% of the transition
      bar: scale().domain([.5, 1])
                  .range([0, 1])
                  .clamp(true),
      // We fade points during the first 5% of the transition
      point: scale().domain([0, .05])
                    .range([0, 1])
                    .clamp(true)
    }
  }

  insert (t = 0) {
    let line = this.serie
      , self = this
      , cloned = line.node().cloneNode(true)
      , newData = line.datum()

    return d3.select(line.node().parentNode)
             .selectAll(`g.serie-transitioning.serie-${line.datum().id}`)
             .data(this.splitter.getSplits())
             .enter()
             .select(function (d) {
               let node = cloned.cloneNode(true)
                 , newPoint = newData.points.filter(p => p.rangeId === d._data.rangeId)[0]
                 , scaledPoints = self.splitter.scalePoints(d.pathPoints, newPoint, newData._columnIdx)

               d.interpolateLine = interpolate(lineGenerator(d.pathPoints), lineGenerator(scaledPoints))

               // TODO comment
               let height = Math.abs(self.yScale(d._data._range[0]) - self.yScale(d._data._range[1])) + scaledPoints[0][1]
                 , o1 = [scaledPoints[0], scaledPoints[0], scaledPoints[scaledPoints.length - 1], scaledPoints[scaledPoints.length - 1]]
                 , o2 = [scaledPoints[0], [scaledPoints[0][0], height], [scaledPoints[scaledPoints.length - 1][0], height], scaledPoints[scaledPoints.length - 1]]

               d.interpolateBar = interpolate(lineGenerator(o1), lineGenerator(o2))

               d3.select(node)
                 .datum(d)
                 .classed('serie-transitioning', true)
                 .classed(`serie-${line.datum().id}`, true)

               d3.select(node)
                 .selectAll('path.line')
                 .attr('d', d.interpolateLine(t))
                 .style('display', '')
                 .style('stroke-width', self.interpolaters.strokeWidth(t))
                 .style('stroke', self.interpolaters.stroke(t))

               d3.select(node)
                 .selectAll('path.outer-line')
                 .style('fill', self.fill)
                 .attr('d', d.interpolateBar(t))
                 .style('opacity', self.interpolaters.barOpacity(t))

               d3.select(node)
                 .selectAll('.point').remove() // Don't show the points for this segment

               this.insertBefore(node, line.node())

               return node
             })
  }

  transform (t, node) {
    let d = d3.select(node).datum()
      , lineT = this.ease(this.scales.line(t)) // Scaled time for the line
      , barT = this.ease(this.scales.bar(t)) // Scaled time for the bar
      , pointT = this.scales.point(t) // Scalred time for the points

    this.serie
        .selectAll('.point')
        .filter(point => point.rangeId == d._data.rangeId)
        .style('opacity', this.interpolaters.pointOpacity(pointT))

    d3.select(node)
      .selectAll('path')
      .style('stroke-width', this.interpolaters.strokeWidth(lineT))

    d3.select(node)
      .selectAll('path.line.data-line')
      .attr('d', d.interpolateLine(lineT))
      .style('stroke', this.interpolaters.stroke(lineT))

    d3.select(node)
      .selectAll('path.outer-line')
      .attr('d', d.interpolateBar(barT))
      .style('opacity', this.interpolaters.barOpacity(barT))
      .style('stroke', this.interpolaters.stroke(barT))
  }

}

export default LineBarTransformer
